import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '@constants/routes';

interface ExternalElearningPage {
  route: string;
  enabled: boolean;
}

const EXTERNAL_ELEARNING_FLOW: ExternalElearningPage[] = [
  { route: ROUTES.EEL_OTP, enabled: true },
  { route: ROUTES.EEL_WELCOME, enabled: true },
  { route: ROUTES.EEL_REGISTER, enabled: true },
  { route: ROUTES.EEL_VIDEO, enabled: true },
  { route: ROUTES.EEL_FINAL, enabled: true }
];

interface ExternalElearningFlowContextValue {
  currentPage: ExternalElearningPage | undefined;
  canGoNext: boolean;
  canGoBack: boolean;
  goNext: () => void;
  goBack: () => void;
  reset: () => void;
}

const ExternalElearningFlowContext =
  createContext<ExternalElearningFlowContextValue | null>(null);

interface ExternalElearningFlowProviderProps {
  children: React.ReactNode;
}

function ExternalElearningFlowProvider({
  children
}: ExternalElearningFlowProviderProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const allowedPages = useMemo(
    () => EXTERNAL_ELEARNING_FLOW.filter((p) => p.enabled),
    []
  );
  const currentIndex = useMemo(
    () => allowedPages.findIndex((p) => p.route === pathname),
    [allowedPages, pathname]
  );

  const goNext = useCallback(() => {
    const next = allowedPages[currentIndex + 1];
    if (next) navigate(next.route);
  }, [allowedPages, currentIndex, navigate]);

  const goBack = useCallback(() => {
    const prev = allowedPages[currentIndex - 1];
    if (prev) navigate(prev.route);
  }, [allowedPages, currentIndex, navigate]);

  const reset = useCallback(() => {
    const start = allowedPages[0];
    if (start) navigate(ROUTES.EEL_WELCOME);
  }, [allowedPages, navigate]);

  const value = useMemo(
    () => ({
      currentPage: allowedPages[currentIndex],
      canGoNext: !!allowedPages[currentIndex + 1],
      canGoBack: !!allowedPages[currentIndex - 1],
      goNext,
      goBack,
      reset
    }),
    [allowedPages, currentIndex, goNext, goBack, reset]
  );

  return (
    <ExternalElearningFlowContext.Provider value={value}>
      {children}
    </ExternalElearningFlowContext.Provider>
  );
}

function useExternalElearningFlow(): ExternalElearningFlowContextValue {
  const ctx = useContext(ExternalElearningFlowContext);
  if (!ctx)
    throw new Error(
      'useExternalElearningFlow must be used within ExternalElearningFlowProvider'
    );
  return ctx;
}

export { ExternalElearningFlowProvider, useExternalElearningFlow };
export type { ExternalElearningFlowContextValue, ExternalElearningPage };
